// @ts-nocheck

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { List, Close, LightArrow, LegalSvg } from '../../assets/svg';
import { Target } from '../../client';
import { NAVBAR_BASE_HEIGHT } from '../../constants/ui/navbar';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { WinamaxLogo } from '../../styled-components/shared';
import Loader from '../Loader';
import PokerButton from '../PokerButton';
import UserInfoContainer from '../UserInfo/UserInfoContainer';

const buttonGestureStyle = css`
  transition: none 150ms ease-in-out;
  transition-property: transform, opacity;

  ${Target.__MOBILE__
    ? undefined
    : css`
        &:hover {
          opacity: 0.9;
        }
      `}

  &:active {
    transform: scale(0.9);
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  background-color: #000;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftWrapper = styled.div`
  height: ${NAVBAR_BASE_HEIGHT}em;
  display: flex;
  justify-content: flex-start;
`;

const RightWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 2.4em;
  width: calc(50% - 2em);
  margin-right: 0.1em;

  @media screen and (max-width: 340px) {
    font-size: 0.9em;
  }

  @media screen and (max-width: 310px) {
    font-size: 0.8em;
  }
`;

const commonBloc = css`
  display: flex;
  justify-content: center;
  align-items: stretch;
`;

const LeftButton = styled.button`
  ${commonBloc}

  width: auto;
  height: 100%;
  min-width: 1.3em;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  outline: none;
  background: none;
  letter-spacing: 1px;

  ${buttonGestureStyle}
`;

const MenuIcon = styled(List)`
  width: 1.3em;
  min-width: 1.3em;
  color: #fff;
  margin-left: 0.6em;
`;

const ChevronLeftIcon = styled(LightArrow)`
  color: #fff;
  width: 0.7em;
  min-width: 0.7em;
  margin: 0 0.6em;
`;

const CloseIcon = styled(Close)`
  width: 1.3em;
  min-width: 1.3em;
  color: #fff;
  margin-left: 0.6em;
`;

const ConnectedButton = styled.div`
  height: 70%;
  margin-right: 0.6em;
  padding: 0.5em 1em;
  border-radius: 100em;
  background-color: ${theme.ConnectButton.backgroundColor};

  font-size: 0.8em;
  color: ${theme.ConnectButton.color};
  text-transform: uppercase;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  ${buttonGestureStyle}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  margin-left: 0.6em;
`;

const LegalIcon = styled(LegalSvg)`
  margin-left: 0.3em;
  height: auto;
`;

const LeftBloc = ({ onGoBack, onClose, onMenuClick, onPokerButtonClick }) => {
  const [play, setPlay] = useState(1);
  const updatePlay = useCallback(() => {
    setPlay((old) => old + 1);
  }, []);

  useEffect(() => {
    let req = null;
    if (onPokerButtonClick) {
      const interval = 5 * 60 * 1000; // 5 min
      req = setInterval(updatePlay, interval);
    }
    return () => {
      if (req) {
        clearInterval(req);
      }
    };
  }, [onPokerButtonClick, updatePlay]);

  const onCloseEnhanced = useCallback(
    (e) => {
      e?.preventDefault?.();
      onClose?.();
    },
    [onClose],
  );

  const onClick = useMemo(() => {
    if (onGoBack) {
      return onGoBack;
    }

    if (onClose) {
      return onCloseEnhanced;
    }

    return onMenuClick;
  }, [onClose, onCloseEnhanced, onGoBack, onMenuClick]);

  const LeftIcon = useMemo(() => {
    if (onGoBack) {
      return ChevronLeftIcon;
    }

    if (onClose) {
      return CloseIcon;
    }

    return MenuIcon;
  }, [onClose, onGoBack]);

  return (
    <>
      <LeftButton onClick={onClick}>
        <LeftIcon />
      </LeftButton>
      <LegalIcon />
      {onPokerButtonClick ? (
        <Center>
          <PokerButton onClick={onPokerButtonClick} animate={play} />
        </Center>
      ) : null}
    </>
  );
};

export type NavigationBarPropsType = {
  isConnected: boolean;
  isConnecting: boolean;
  showConnectButton: boolean;
  onMenuClick: () => void;
  onLogoClick: () => void;
  onGoBack: () => void;
  onClose: () => void;
  onConnectButtonClick: () => void;
  onUserClick: () => void;
  onPokerButtonClick: () => void;
  showUserInfo?: boolean;
  className?: string;
};

const NavigationBar = ({
  className,
  isConnected,
  isConnecting,
  onPokerButtonClick,
  onConnectButtonClick,
  onLogoClick,
  onGoBack,
  onClose,
  onMenuClick,
  onUserClick,
  showConnectButton,
  showUserInfo = true,
}: NavigationBarPropsType) => {
  const renderRight = useCallback(() => {
    if (isConnected) {
      return showUserInfo ? <UserInfoContainer onUserClick={onUserClick} /> : null;
    }
    if (isConnecting) {
      return <Loader />;
    }
    if (showConnectButton) {
      return <ConnectedButton onClick={onConnectButtonClick}>{__('Log in')}</ConnectedButton>;
    }
  }, [isConnected, isConnecting, onConnectButtonClick, onUserClick, showConnectButton, showUserInfo]);

  return (
    <Wrapper className={className}>
      <LeftWrapper>
        <LeftBloc
          onClose={onClose}
          onGoBack={onGoBack}
          onMenuClick={onMenuClick}
          onPokerButtonClick={onPokerButtonClick}
        />
      </LeftWrapper>
      <WinamaxLogo onClick={onLogoClick} />
      <RightWrapper>{renderRight()}</RightWrapper>
    </Wrapper>
  );
};

export default React.memo<NavigationBarPropsType>(NavigationBar);
